import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const RECOVERY_TOAST_ID = 'sse-stale-session-recovery';
const GRACE_PERIOD_MS = 2 * 60 * 1000;

let inFlight = false;
let graceTimer = null;

const formatCountdown = (msRemaining) => {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

function StaleSessionToast({ deadline, onLogoutNow }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, deadline - now);

  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Session expired</div>
      <div style={{ marginBottom: 8 }}>
        Please log out and log back in to continue. You will be logged out automatically in{' '}
        <strong>{formatCountdown(remaining)}</strong>.
      </div>
      <button
        type="button"
        onClick={onLogoutNow}
        style={{
          padding: '6px 12px',
          background: '#fff',
          color: '#b91c1c',
          border: '1px solid #b91c1c',
          borderRadius: 4,
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        Log out
      </button>
    </div>
  );
}

const clearGraceTimer = () => {
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
};

async function probeRefresh(apiUrl) {
  try {
    const res = await fetch(`${apiUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.status === 200) return 'recovered';
    if (res.status === 401) return 'auth-failed';
    return 'auth-failed';
  } catch {
    return 'network-error';
  }
}

/**
 * Attempts to recover a stale SSE session.
 *
 * Bypasses apiClient on purpose — the global response interceptor would
 * call logout() on a 401 from the refresh endpoint before this flow
 * could show its toast or start the grace timer.
 *
 * Returns one of:
 *  - 'recovered'      refresh succeeded; caller should resume normal operation
 *  - 'auth-failed'    refresh rejected; toast is up, grace timer is running
 *  - 'network-error'  refresh could not reach server; caller should keep retrying
 *  - 'in-flight'      another recovery is already running; caller should back off
 */
export const attemptStaleSessionRecovery = async ({ apiUrl, onRecovered, onForceLogout }) => {
  if (inFlight) return 'in-flight';
  inFlight = true;

  try {
    const result = await probeRefresh(apiUrl);

    if (result === 'recovered') {
      toast.dismiss(RECOVERY_TOAST_ID);
      onRecovered?.();
      return 'recovered';
    }

    if (result === 'network-error') {
      return 'network-error';
    }

    const triggerLogout = () => {
      clearGraceTimer();
      toast.dismiss(RECOVERY_TOAST_ID);
      onForceLogout?.();
    };

    const deadline = Date.now() + GRACE_PERIOD_MS;

    toast.error(<StaleSessionToast deadline={deadline} onLogoutNow={triggerLogout} />, {
      toastId: RECOVERY_TOAST_ID,
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    });

    clearGraceTimer();
    graceTimer = setTimeout(triggerLogout, GRACE_PERIOD_MS);

    return 'auth-failed';
  } finally {
    inFlight = false;
  }
};
